/**
 * Restaurant & food service supply — catalog render, tier preview, quote form.
 */

type RuntimeConfig = { apiEndpoint?: string };

type CatalogProduct = {
  id: string;
  name: string;
  species: string;
  description: string;
  image?: string;
  customPrice: number;
  wholesalePrice: number | null;
  moq: number | null;
};

type Catalog = {
  discounts: { order1: number; order2: number; order3: number };
  copy: {
    proofTurnaroundDays: number;
    careNote: string;
    moqFraming: string;
  };
  products: CatalogProduct[];
};

let apiBase = "";
let configLoaded = false;
let catalog: Catalog | null = null;
let lastTierPct: number | null = null;

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

async function loadConfig(): Promise<void> {
  try {
    const res = await fetch("config.json", { cache: "no-store" });
    if (!res.ok) throw new Error(String(res.status));
    const cfg = (await res.json()) as RuntimeConfig;
    apiBase = (cfg.apiEndpoint ?? "").replace(/\/$/, "");
    configLoaded = true;
  } catch (err) {
    console.warn("[restaurant] config", err);
    configLoaded = false;
  }
}

async function loadCatalog(): Promise<void> {
  const res = await fetch("restaurant-catalog.json", { cache: "no-store" });
  if (!res.ok) throw new Error("Could not load catalog.");
  catalog = (await res.json()) as Catalog;
}

function money(n: number): string {
  return `$${Math.round(n)}`;
}

function renderCatalog(): void {
  if (!catalog) return;
  const rows = document.querySelector("#product-rows");
  const moqRows = document.querySelector("#moq-rows");
  const picker = document.querySelector("#line-picker");
  if (!rows || !moqRows || !picker) return;

  const d = catalog.discounts;
  const d1 = document.querySelector("#d1");
  const d2 = document.querySelector("#d2");
  const d3 = document.querySelector("#d3");
  if (d1) d1.textContent = `${d.order1}%`;
  if (d2) d2.textContent = `${d.order2}%`;
  if (d3) d3.textContent = `${d.order3}%`;

  const proof = document.querySelector("#proof-days");
  if (proof) proof.textContent = String(catalog.copy.proofTurnaroundDays);
  const care = document.querySelector("#care-note");
  if (care) care.textContent = catalog.copy.careNote;
  const moqFrame = document.querySelector("#moq-framing");
  if (moqFrame) moqFrame.textContent = catalog.copy.moqFraming;

  rows.innerHTML = catalog.products
    .map((p) => {
      const price =
        p.wholesalePrice == null
          ? `<p class="product-row__price">${escapeHtml(money(p.customPrice))}<span class="product-row__price-note">Custom only</span></p>`
          : `<p class="product-row__price">${escapeHtml(money(p.wholesalePrice))}–${escapeHtml(money(p.customPrice))}<span class="product-row__price-note">Wholesale–custom</span></p>`;
      const thumb = p.image
        ? `<img
            class="product-row__img"
            src="${escapeHtml(p.image)}"
            alt=""
            width="140"
            height="100"
            loading="lazy"
            decoding="async"
          />`
        : `<span class="product-row__img product-row__img--empty" aria-hidden="true"></span>`;
      return `<li class="product-row">
        ${thumb}
        <div>
          <h3 class="product-row__name">${escapeHtml(p.name)}</h3>
          <p class="product-row__desc">${escapeHtml(p.description)}</p>
          <p class="product-row__meta">${escapeHtml(p.species)}</p>
        </div>
        ${price}
      </li>`;
    })
    .join("");

  moqRows.innerHTML = catalog.products
    .map((p) => {
      const moq =
        p.moq == null ? "Not offered wholesale" : `${p.moq} units`;
      return `<li class="moq-row">
        <span>${escapeHtml(p.name)}</span>
        <strong>${escapeHtml(moq)}</strong>
      </li>`;
    })
    .join("");

  picker.innerHTML = catalog.products
    .map(
      (p) => `<div class="line-row">
        <label class="check">
          <input type="checkbox" name="product" value="${escapeHtml(p.id)}" data-product="${escapeHtml(p.id)}" />
          ${escapeHtml(p.name)}
        </label>
        <span></span>
        <input
          class="field-input field-input--qty"
          type="number"
          min="1"
          value="1"
          name="qty-${escapeHtml(p.id)}"
          data-qty-for="${escapeHtml(p.id)}"
          disabled
          aria-label="Quantity for ${escapeHtml(p.name)}"
        />
      </div>`,
    )
    .join("");

  picker.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-product]').forEach(
    (cb) => {
      cb.addEventListener("change", () => {
        const qty = picker.querySelector<HTMLInputElement>(
          `input[data-qty-for="${cb.dataset.product}"]`,
        );
        if (qty) qty.disabled = !cb.checked;
      });
    },
  );
}

function setupOtherLine(): void {
  const check = document.querySelector<HTMLInputElement>("#other-check");
  const label = document.querySelector<HTMLInputElement>("#other-label");
  const qty = document.querySelector<HTMLInputElement>("#other-qty");
  if (!check || !label || !qty) return;
  check.addEventListener("change", () => {
    label.disabled = !check.checked;
    qty.disabled = !check.checked;
  });
}

function setupNavToggle(): void {
  const toggle = document.querySelector<HTMLButtonElement>(".nav__toggle");
  const menu = document.querySelector<HTMLElement>("#nav-menu");
  if (!toggle || !menu) return;
  toggle.addEventListener("click", () => {
    const open = menu.dataset.open === "true";
    menu.dataset.open = open ? "false" : "true";
    toggle.setAttribute("aria-expanded", open ? "false" : "true");
  });
}

function setStatus(
  el: HTMLElement,
  state: "idle" | "loading" | "success" | "error",
  message: string,
): void {
  el.hidden = false;
  el.dataset.state = state;
  el.textContent = message;
}

function updateTierUi(pct: number, count: number): void {
  lastTierPct = pct;
  const callout = document.querySelector("#tier-callout");
  const hint = document.querySelector("#form-tier-hint");
  const text =
    pct > 0
      ? `Your next confirmed order: ${pct}% off (${count} confirmed order${count === 1 ? "" : "s"} on file).`
      : `Your next confirmed order: standard pricing (${count} confirmed orders on file — loyalty ladder complete).`;
  if (callout) callout.textContent = text;
  if (hint) hint.textContent = text;
}

async function fetchTier(email: string): Promise<void> {
  if (!apiBase || !email.includes("@")) return;
  try {
    const res = await fetch(`${apiBase}/api/restaurant/tier-preview`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) return;
    const data = (await res.json()) as {
      nextDiscountPercent?: number;
      confirmedOrderCount?: number;
    };
    if (typeof data.nextDiscountPercent === "number") {
      updateTierUi(data.nextDiscountPercent, data.confirmedOrderCount ?? 0);
    }
  } catch (err) {
    console.warn("[restaurant] tier preview", err);
  }
}

function setupTierPreview(): void {
  const emailInput = document.querySelector<HTMLInputElement>("#email");
  if (!emailInput) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const run = () => {
    const email = emailInput.value.trim();
    if (EMAIL_RE.test(email)) void fetchTier(email);
  };
  emailInput.addEventListener("blur", run);
  emailInput.addEventListener("input", () => {
    clearTimeout(timer);
    timer = setTimeout(run, 400);
  });
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function collectLines(): Array<{ id: string; quantity: number; label?: string }> {
  const lines: Array<{ id: string; quantity: number; label?: string }> = [];
  document
    .querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-product]:checked')
    .forEach((cb) => {
      const id = cb.dataset.product ?? "";
      const qtyEl = document.querySelector<HTMLInputElement>(
        `input[data-qty-for="${id}"]`,
      );
      const quantity = Number(qtyEl?.value ?? 1);
      if (id && quantity >= 1) lines.push({ id, quantity });
    });
  const otherCheck = document.querySelector<HTMLInputElement>("#other-check");
  if (otherCheck?.checked) {
    const label =
      document.querySelector<HTMLInputElement>("#other-label")?.value.trim() ?? "";
    const quantity = Number(
      document.querySelector<HTMLInputElement>("#other-qty")?.value ?? 1,
    );
    if (label && quantity >= 1) {
      lines.push({ id: "other", label, quantity });
    }
  }
  return lines;
}

function setupForm(): void {
  const form = document.querySelector<HTMLFormElement>("#restaurant-form");
  if (!form) return;
  const status = form.querySelector<HTMLElement>("#form-status");
  const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    if (!status || !submitBtn) return;

    const company = String(new FormData(form).get("company") ?? "").trim();
    if (company) {
      form.reset();
      setStatus(
        status,
        "success",
        "Thanks. Your quote request is on its way. We'll be in touch within 2 business days.",
      );
      return;
    }

    const fd = new FormData(form);
    const name = String(fd.get("name") ?? "").trim();
    const businessName = String(fd.get("businessName") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    const phone = String(fd.get("phone") ?? "").trim();
    const message = String(fd.get("message") ?? "").trim();
    const lines = collectLines();

    if (!name || !businessName || !EMAIL_RE.test(email) || message.length < 10) {
      setStatus(status, "error", "Please complete name, business, email, and a short message.");
      return;
    }
    if (lines.length === 0) {
      setStatus(status, "error", "Select at least one item with a quantity.");
      return;
    }
    if (!configLoaded) {
      setStatus(
        status,
        "error",
        "Form is not configured yet. Please email us directly.",
      );
      return;
    }

    submitBtn.disabled = true;
    setStatus(status, "loading", "Sending your quote request…");

    try {
      const res = await fetch(`${apiBase}/contact`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          source: "restaurant",
          name,
          businessName,
          email,
          phone,
          lines,
          message,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        nextDiscountPercent?: number;
      };
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      form.reset();
      document
        .querySelectorAll<HTMLInputElement>("input[data-qty-for]")
        .forEach((el) => {
          el.disabled = true;
        });
      const otherLabel = document.querySelector<HTMLInputElement>("#other-label");
      const otherQty = document.querySelector<HTMLInputElement>("#other-qty");
      if (otherLabel) otherLabel.disabled = true;
      if (otherQty) otherQty.disabled = true;

      const pct =
        typeof data.nextDiscountPercent === "number"
          ? data.nextDiscountPercent
          : lastTierPct;
      const extra =
        pct != null
          ? ` Your next confirmed order may qualify for ${pct}% off.`
          : "";
      setStatus(
        status,
        "success",
        `Thanks. Your quote request is on its way. We'll be in touch within 2 business days.${extra}`,
      );
      void fetchTier(email);
    } catch (err) {
      console.error("[restaurant] submit failed", err);
      setStatus(
        status,
        "error",
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      submitBtn.disabled = false;
    }
  });
}

async function boot(): Promise<void> {
  setupNavToggle();
  await loadConfig();
  await loadCatalog();
  renderCatalog();
  setupOtherLine();
  setupTierPreview();
  setupForm();
}

void boot();
